import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { config } from '@/lib/config'

// Short-lived, in-memory snapshots only. Never persist operational data in local
// storage or share it across users/API origins. A revisit revalidates immediately.
interface Entry { value?: unknown; at: number; pending?: Promise<unknown> }
const entries = new Map<string, Entry>()
const MAX_AGE = 5 * 60_000

function entryFor(key: string): Entry {
  let entry = entries.get(key)
  if (entry && !entry.pending && Date.now() - entry.at > MAX_AGE) {
    entries.delete(key)
    entry = undefined
  }
  if (!entry) {
    entry = { at: Date.now() }
    entries.set(key, entry)
    if (entries.size > 32) {
      const oldest = [...entries.keys()].find(k => k !== key && !entries.get(k)?.pending)
      if (oldest) entries.delete(oldest)
    }
  }
  return entry
}

function fetchEntry<T>(key: string, path: string): Promise<T> {
  const entry = entryFor(key)
  if (!entry.pending) {
    entry.pending = api.get<T>(path).catch(async error => {
      // Retry this read once after a transient gateway/throttling failure. Writes
      // are deliberately outside this hook and must never be replayed here.
      if (!(error instanceof ApiError) || ![429, 502, 503, 504].includes(error.status)) throw error
      await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 500))
      return api.get<T>(path)
    }).then(value => {
      entry.value = value
      entry.at = Date.now()
      return value
    }).finally(() => { entry.pending = undefined })
  }
  return entry.pending as Promise<T>
}

/** Reuse a board during navigation and coalesce overlapping polls/StrictMode mounts. */
export function useBoardData<T>(path: string, retainErrorOnReload = false) {
  const { user } = useAuth()
  const key = JSON.stringify([config.apiUrl, user?.id, user?.role, path])
  const generation = useRef(0)
  const [state, setState] = useState<{ key: string; data: T | null; error: string | null; loading: boolean }>(() => ({
    key, data: entryFor(key).value as T ?? null, error: null, loading: !entryFor(key).value,
  }))
  const reload = useCallback(async (fresh = false) => {
    const current = ++generation.current
    setState(old => ({ key, data: old.key === key ? old.data : entryFor(key).value as T ?? null, error: retainErrorOnReload && old.key === key ? old.error : null, loading: true }))
    try {
      if (fresh) await entryFor(key).pending?.catch(() => {})
      const data = await fetchEntry<T>(key, path)
      if (current === generation.current) setState({ key, data, error: null, loading: false })
    } catch (error) {
      if (current === generation.current) setState(old => ({ ...old, error: (error as Error).message, loading: false }))
    }
  }, [key, path, retainErrorOnReload])
  useEffect(() => { void reload(); return () => { generation.current++ } }, [reload])
  useEffect(() => {
    const refresh = () => { if (!document.hidden) void reload(true) }
    window.addEventListener('board-data-changed', refresh)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('board-data-changed', refresh)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [reload])
  // A user or route switch must never paint the previous key's data for one frame.
  return { ...(state.key === key ? state : { data: entryFor(key).value as T ?? null, error: null, loading: true }), receivedAt: entryFor(key).value ? entryFor(key).at : null, reload }
}
